import { useAttendanceTable } from "./attendanceTable/useAttendanceTable";
import { translate, TranslatorKeys } from "@/config/translator";
import { clsx } from "clsx/lite";
import { EmployeeAttendanceRow } from "./attendanceTable/employeeAttendanceRow";
import { AttendanceNoteDialog } from "./attendanceTable/attendanceNoteDialog";
import { TempAttendanceMonth } from "@/config/types";
import { AttendanceEmployee, AttendanceMonth } from "@/api/openapi";
import {
  makeCellId,
  onMouseEnterCell,
  onMouseLeaveCell,
} from "./attendanceTable/employeeAttendanceRow/useEmployeeAttendanceRow";

type AttendanceTableProps = {
  tempAttendance: TempAttendanceMonth | undefined;
  openVoci: (employee: AttendanceEmployee) => void;
  openRestoreEmployeeDialog: (employee: AttendanceEmployee) => void;
  onSaveEdit: (isDraft: boolean, attendances?: AttendanceMonth) => void;
  isSaving: boolean;
};

export function AttendanceTable({
  tempAttendance,
  openVoci,
  onSaveEdit,
  openRestoreEmployeeDialog,
  isSaving,
}: AttendanceTableProps) {
  const {
    lang,
    setExpandedEmployee,
    noteOpenEmployee,
    note,
    setNote,
    onSaveNote,
    onCloseNote,
    noteDialogOpen,
    onOpenNote,
    selectedMonth,
    selectedYear,
    expandedEmployeeIndex,
  } = useAttendanceTable();

  return (
    // Single scroll container: x-scrolling moves the day columns while the
    // sticky cells (#, employee, sum) stay pinned; y-scrolling moves the rows
    // while the sticky header stays pinned.
    <div
      className={clsx(
        "max-w-fit",
        "h-[calc(100vh-19rem)]",
        "overflow-x-auto",
        "overflow-y-scroll",
        "text-base",
        "rounded-xl",
      )}
    >
      <table className="border-separate border-spacing-0 table-fixed">
        <thead>
          <tr className="h-12">
            {/* '#' HEADER CELL */}
            <th
              className={clsx(
                "sticky",
                "top-0",
                "left-0",
                "z-30",
                "w-14",
                "p-2",
                "font-normal",
                "text-font-secondary",
                "bg-canvas-secondary",
                "border",
                "border-neutral",
                "rounded-tl-xl",
                "overflow-hidden",
                "text-center",
              )}
              id={makeCellId(-1, -2, null)}
              onMouseEnter={() => onMouseEnterCell(-1, -2, null, null, null)}
              onMouseLeave={() => onMouseLeaveCell(-1, -2, null, null, null)}
            >
              #
            </th>

            {/* 'Employee' HEADER CELL */}
            <th
              className={clsx(
                "sticky",
                "top-0",
                "left-14",
                "z-30",
                "min-w-67",
                "p-2",
                "font-normal",
                "text-left",
                "border-t",
                "border-b",
                "border-r",
                "border-neutral",
                "text-font-secondary",
                "bg-canvas-secondary",
              )}
              id={makeCellId(-1, -1, null)}
              onMouseEnter={() => onMouseEnterCell(-1, -1, null, null, null)}
              onMouseLeave={() => onMouseLeaveCell(-1, -1, null, null, null)}
            >
              {translate("employee", lang)}
            </th>

            {/* DAYS HEADER CELLS (sticky top only) */}
            {tempAttendance?.attendances[0].days.map(
              (day, dayIndex, daysArr) => (
                <th
                  key={`day-header-${dayIndex}`}
                  className={clsx(
                    "sticky",
                    "top-0",
                    "z-20",
                    "w-14",
                    "font-normal",
                    "text-center",
                    "border-t",
                    "border-b",
                    dayIndex !== daysArr.length - 1 && "border-r",
                    "border-neutral",
                    day.isHoliday ? "bg-neutral" : "bg-canvas-secondary",
                    "text-font-secondary",
                  )}
                  id={makeCellId(-1, dayIndex, null)}
                  onMouseEnter={() =>
                    onMouseEnterCell(-1, dayIndex, null, null, null)
                  }
                  onMouseLeave={() =>
                    onMouseLeaveCell(-1, dayIndex, null, null, null)
                  }
                >
                  <div className="flex flex-col leading-4 text-sm">
                    <div>{String(dayIndex + 1).padStart(2, "0")}</div>
                    {selectedYear !== undefined && selectedMonth !== undefined
                      ? translate(
                          `weekdayShort${new Date(
                            selectedYear,
                            selectedMonth - 1,
                            dayIndex,
                          ).getDay()}` as TranslatorKeys,
                          lang,
                        )
                      : ""}
                  </div>
                </th>
              ),
            )}

            {/* 'Sum' HEADER CELL */}
            <th
              className={clsx(
                "sticky",
                "top-0",
                "right-30",
                "z-30",
                "w-16",
                "p-2",
                "font-normal",
                "text-center",
                "border-t",
                "border-b",
                "border-l",
                "border-neutral",
                "bg-canvas-secondary",
                "text-font-secondary",
              )}
            >
              {translate("sum", lang)}
            </th>

            {/* 'Action' HEADER CELL */}
            <th
              className={clsx(
                "sticky",
                "top-0",
                "right-0",
                "z-30",
                "w-30",
                "min-w-30",
                "p-2",
                "font-normal",
                "text-center",
                "border-t",
                "border-b",
                "border-l",
                "border-r",
                "border-neutral",
                "bg-canvas-secondary",
                "text-font-secondary",
                "rounded-tr-xl",
              )}
            >
              {translate("action", lang)}
            </th>
          </tr>
        </thead>

        <tbody>
          {tempAttendance?.attendances.map(
            (employeeAttendance, index, attendancesArray) => (
              <EmployeeAttendanceRow
                key={`employee-attendance-row-${employeeAttendance.employee.matricola}`}
                isSaving={isSaving}
                openRestoreEmployeeDialog={openRestoreEmployeeDialog}
                isLastRow={index === attendancesArray.length - 1}
                employeeAttendance={employeeAttendance}
                index={index}
                isExpanded={
                  expandedEmployeeIndex !== null &&
                  index === expandedEmployeeIndex
                }
                expandedEmployeeIndex={expandedEmployeeIndex}
                setExpandedEmployee={setExpandedEmployee}
                onNoteOpen={() => onOpenNote(employeeAttendance.employee)}
                onVociOpen={() => openVoci(employeeAttendance.employee)}
                onSaveEdit={onSaveEdit}
              />
            ),
          )}
        </tbody>
      </table>
      <AttendanceNoteDialog
        note={note}
        setNote={setNote}
        onSave={onSaveNote}
        open={noteDialogOpen}
        onClose={onCloseNote}
        name={noteOpenEmployee?.name ?? ""}
      />
    </div>
  );
}
